// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  async function register(email, password, name) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', cred.user.uid), {
      email,
      name,
      plan: 'free',
      addCount: 0,
      editCount: 0,
      createdAt: new Date().toISOString(),
      paymentDate: null,
      renewalDate: null,
    });
    return cred;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  async function fetchUserPlan(uid) {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const data = snap.data();
      // Check if paid plan has expired
      if (data.plan === 'paid' && data.renewalDate) {
        const renewal = new Date(data.renewalDate);
        if (new Date() > renewal) {
          await updateDoc(doc(db, 'users', uid), { plan: 'free' });
          data.plan = 'free';
        }
      }
      setUserPlan(data);
      return data;
    }
    return null;
  }

  async function incrementAddCount() {
    if (!currentUser) return;
    const ref = doc(db, 'users', currentUser.uid);
    const newCount = (userPlan?.addCount || 0) + 1;
    await updateDoc(ref, { addCount: newCount });
    setUserPlan(prev => ({ ...prev, addCount: newCount }));
  }

  async function incrementEditCount() {
    if (!currentUser) return;
    const ref = doc(db, 'users', currentUser.uid);
    const newCount = (userPlan?.editCount || 0) + 1;
    await updateDoc(ref, { editCount: newCount });
    setUserPlan(prev => ({ ...prev, editCount: newCount }));
  }

  async function activatePaidPlan(paymentId, additionLimit) {
    if (!currentUser) return;
    const paymentDate = new Date().toISOString();
    const renewal = new Date();
    renewal.setFullYear(renewal.getFullYear() + 1);
    const renewalDate = renewal.toISOString();
    await updateDoc(doc(db, 'users', currentUser.uid), {
      plan: 'paid',
      paymentDate,
      renewalDate,
      paymentId,
      additionLimit,
      addCount: 0, // Reset manual add count for the new subscription
    });
    setUserPlan(prev => ({ 
      ...prev, 
      plan: 'paid', 
      paymentDate, 
      renewalDate, 
      paymentId, 
      additionLimit, 
      addCount: 0 
    }));
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) await fetchUserPlan(user.uid);
      else setUserPlan(null);
      setLoading(false);
    });
    return unsub;
  }, []);

  const isPaid = userPlan?.plan === 'paid';
  const canAdd = isPaid 
    ? (userPlan?.addCount || 0) < (userPlan?.additionLimit || 100)
    : (userPlan?.addCount || 0) < 5;
  const canEdit = true; // Edits are unlimited as requested

  const value = {
    currentUser, userPlan, isPaid, canAdd, canEdit,
    register, login, logout,
    incrementAddCount, incrementEditCount, activatePaidPlan, fetchUserPlan,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
